<template>
  <div>
    <Teleport v-if="mounted" to="#header-breadcrumbs">
      <CBreadcrumb v-bind="{ routes }" />
    </Teleport>
    <CardWrapper class="p-4 md:p-9">
      <div
        class="flex-y-center justify-between flex-col md:flex-row flex-wrap gap-4 mb-6 md:mb-12"
      >
        <NotificationTab @handleUpdateTab="changeNotificationType" />
        <div class="w-full md:w-auto flex-y-center gap-2">
          <CButton
            variant="dark"
            :text="$t('read_all')"
            class="md:w-max"
            @click="handleReadAllNotifications"
          />
          <CButton
            variant="dark"
            :text="$t('clear_all')"
            class="md:w-max"
            v-if="false"
          />
        </div>
      </div>
      <Transition name="fade" mode="out-in">
        <div :key="loading">
          <template v-if="loading">
            <NotificationShimmer v-for="el of 8" :key="el" />
          </template>
          <template v-else>
            <template v-if="notifications?.length">
              <NotificationElement
                v-for="(notification, index) in notifications"
                :key="index"
                v-bind="{ notification }"
                @click="
                  notificationStore.getNotificationsSingle(
                    notification?.id,
                    'page',
                  )
                "
              />
              <div ref="more" />
              <Transition name="fade" mode="out-in">
                <div v-if="loadingMore">
                  <div class="w-full flex-y-center justify-center mt-4">
                    <div class="dots-loading" />
                  </div>
                </div>
              </Transition>
            </template>
            <template v-else>
              <CNodata
                image="/images/svg/no-data/46.svg"
                :title="$t('no_notifications_title')"
                :subtitle="$t('no_notifications_desc')"
              />
            </template>
          </template>
        </div>
      </Transition>
    </CardWrapper>
  </div>
</template>

<script setup lang="ts">
import CBreadcrumb from "@/components/Common/CBreadcrumb.vue";
import CardWrapper from "@/components/Card/CardWrapper.vue";
import { useMounted } from "@/composables/useMounted";
import { computed, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import NotificationElement from "@/modules/Notification/components/NotificationElement.vue";
import NotificationTab from "@/modules/Notification/components/NotificationTab.vue";
import CButton from "@/components/Common/CButton.vue";
import { useNotificationStore } from "@/modules/Notification/stores";
import NotificationShimmer from "@/modules/Notification/components/NotificationShimmer.vue";
import CNodata from "@/components/Common/CNodata.vue";
import { useIntersectionObserver } from "@vueuse/core";

const { mounted } = useMounted();
const { t } = useI18n();
const notificationStore = useNotificationStore();
const more = ref(null);
const loadingMore = ref(false);

const notifications = computed(() => notificationStore.notifications);

const loading = computed(() => notificationStore.loading);
const pagesCount = computed(() => notificationStore.notificationsCount);

const routes = computed(() => [
  {
    name: t("profile.notification"),
    route: "/notifications",
  },
]);

notificationStore.getNotifications({ size: notificationStore.size.page });

const handleReadAllNotifications = () => {
  notificationStore.readAllNotifications();
  notificationStore.getNotifications({ size: notificationStore.size.page });
  notificationStore.getHeaderNotifications();
};

const changeNotificationType = (value: "robot" | "plan" | "wallet") => {
  notificationStore.getNotifications({
    notification__type: value,
    size: notificationStore.size.page,
  });
};

const loadMoreNotifications = () => {
  notificationStore.size.page += 5;
  notificationStore
    .getNotifications({ size: notificationStore.size.page })
    .finally(() => {
      loadingMore.value = false;
    });
};
onMounted(() => {
  useIntersectionObserver(more, ([{ isIntersecting }]) => {
    if (notificationStore.size.page < pagesCount.value && isIntersecting) {
      loadingMore.value = isIntersecting;
      setTimeout(() => {
        loadMoreNotifications();
      }, 500);
    }
  });
});
</script>

<style scoped>
.dots-loading {
  width: 56px;
  height: 26.9px;
  background:
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 0% 50%,
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 50% 50%,
    radial-gradient(circle closest-side, #16cc53 90%, #0000) 100% 50%;
  background-size: calc(100% / 3) 13.4px;
  background-repeat: no-repeat;
  animation: dots-7ar3yq 0.8s infinite linear;
}

@keyframes dots-7ar3yq {
  20% {
    background-position:
      0% 0%,
      50% 50%,
      100% 50%;
  }

  40% {
    background-position:
      0% 100%,
      50% 0%,
      100% 50%;
  }

  60% {
    background-position:
      0% 50%,
      50% 100%,
      100% 0%;
  }

  80% {
    background-position:
      0% 50%,
      50% 50%,
      100% 100%;
  }
}
</style>
